<template>
    <div class="input-wrap" :class="{ 'input-wrap--expanded': isExpanded }">
        <div class="input-inner">
            <div
                class="input-card"
                :class="{
                    'input-card--disabled': isLimitReached,
                    'input-card--expanded': isExpanded,
                }"
                ref="cardRef"
                @click="expand"
                @pointerdown.capture="onCardPointerDown"
            >
                <div class="input-row">
                    <button
                        v-if="!isExpanded"
                        type="button"
                        class="expand-btn"
                        aria-label="Развернуть"
                        @click.stop="expand"
                    >
                        <ChatIcon name="plus" :size="14" />
                    </button>

                    <textarea
                        ref="textareaRef"
                        v-model="text"
                        class="input-field"
                        :class="{ 'input-field--compact': !isExpanded }"
                        rows="1"
                        :placeholder="placeholder"
                        :disabled="isLimitReached"
                        @keydown="onKeydown"
                        @input="onInput"
                        @focus="onFocus"
                        @blur="onBlur"
                    />

                    <button
                        v-if="isStreaming"
                        type="button"
                        class="send-btn send-btn--stop"
                        aria-label="Остановить генерацию"
                        @click.stop="$emit('stop')"
                    >
                        <ChatIcon name="stop" :size="12" color="#FFFFFF" />
                    </button>
                    <button
                        v-else
                        type="button"
                        class="send-btn"
                        :disabled="!canSend"
                        aria-label="Отправить"
                        @click.stop="send"
                    >
                        <ChatIcon name="arrow-up" :size="16" color="#FFFFFF" />
                    </button>
                </div>

                <div v-if="isExpanded" class="input-toolbar">
                    <div class="toolbar-left">
                        <div
                            class="tool-select"
                            :class="{ 'tool-select--active': isRegionActive }"
                            @click.stop
                        >
                            <ChatIcon name="map-pin" :size="15" />
                            <button
                                type="button"
                                class="filter-trigger"
                                aria-label="Место"
                                @click.stop="toggleDropdown('region')"
                            >
                                {{ selectedRegionLabel }}
                            </button>
                            <button
                                v-if="isRegionActive"
                                type="button"
                                class="filter-reset"
                                aria-label="Сбросить регион"
                                @click.stop="resetFilter('region')"
                            >
                                ×
                            </button>
                            <div
                                v-if="openDropdown === 'region'"
                                class="filter-dropdown"
                                @click.stop
                            >
                                <input
                                    ref="regionInputRef"
                                    v-model="regionSearch"
                                    class="region-search"
                                    type="text"
                                    placeholder="Поиск региона"
                                    aria-label="Поиск региона"
                                    @keydown.stop
                                    @keydown.enter.prevent="selectFirstRegion"
                                />
                                <p v-if="filteredRegionOptions.length === 0" class="region-search-hint">
                                    {{ regionEmptyHint }}
                                </p>
                                <button
                                    v-for="region in filteredRegionOptions"
                                    :key="region.value"
                                    type="button"
                                    class="filter-option"
                                    :class="{ 'filter-option--selected': localFilters.region === region.value }"
                                    @click.stop="selectRegion(region.value)"
                                >
                                    <span>{{ region.label }}</span>
                                </button>
                            </div>
                        </div>
                        <label
                            class="tool-select"
                            :class="{ 'tool-select--active': isPeriodActive }"
                            @click.stop
                        >
                            <ChatIcon name="calendar" :size="15" />
                            <button
                                type="button"
                                class="filter-trigger"
                                aria-label="Период"
                                @click.stop="toggleDropdown('period')"
                            >
                                {{ selectedPeriodLabel }}
                            </button>
                            <button
                                v-if="isPeriodActive"
                                type="button"
                                class="filter-reset"
                                aria-label="Сбросить период"
                                @click.stop="resetFilter('period')"
                            >
                                ×
                            </button>
                            <div
                                v-if="openDropdown === 'period'"
                                class="filter-dropdown filter-dropdown--period"
                                @click.stop
                            >
                                <div class="period-grid">
                                    <label class="period-field">
                                        <span>Год</span>
                                        <select
                                            v-model.number="periodDraft.year"
                                            class="dropdown-select"
                                            aria-label="Год"
                                            @change="onPeriodYearChange"
                                        >
                                            <option v-for="year in yearOptions" :key="year" :value="year">
                                                {{ year }}
                                            </option>
                                        </select>
                                    </label>
                                    <label class="period-field">
                                        <span>С</span>
                                        <select
                                            v-model.number="periodDraft.monthFrom"
                                            class="dropdown-select"
                                            aria-label="Месяц с"
                                            @change="onMonthFromChange"
                                        >
                                            <option v-for="month in monthOptions" :key="month.value" :value="month.value">
                                                {{ month.label }}
                                            </option>
                                        </select>
                                    </label>
                                    <label class="period-field">
                                        <span>По</span>
                                        <select
                                            v-model.number="periodDraft.monthTo"
                                            class="dropdown-select"
                                            aria-label="Месяц по"
                                            @change="onMonthToChange"
                                        >
                                            <option v-for="month in monthOptions" :key="month.value" :value="month.value">
                                                {{ month.label }}
                                            </option>
                                        </select>
                                    </label>
                                </div>
                            </div>
                        </label>
                        <label
                            class="tool-select"
                            :class="{ 'tool-select--active': isSourceActive }"
                            @click.stop
                        >
                            <ChatIcon :name="selectedSourceIcon" :size="15" />
                            <button
                                type="button"
                                class="filter-trigger"
                                aria-label="Источник"
                                @click.stop="toggleDropdown('source')"
                            >
                                {{ selectedSourceLabel }}
                            </button>
                            <button
                                v-if="isSourceActive"
                                type="button"
                                class="filter-reset"
                                aria-label="Сбросить источник"
                                @click.stop="resetFilter('source')"
                            >
                                ×
                            </button>
                            <div
                                v-if="openDropdown === 'source'"
                                class="filter-dropdown"
                                @click.stop
                            >
                                <button
                                    v-for="source in sourceOptions"
                                    :key="source.id"
                                    type="button"
                                    class="filter-option filter-option--with-icon filter-option--multi"
                                    :class="{ 'filter-option--selected': isSourceOptionSelected(source.id) }"
                                    @click.stop="toggleSource(source.id)"
                                >
                                    <span
                                        class="filter-option-check"
                                        :class="{ 'filter-option-check--on': isSourceOptionSelected(source.id) }"
                                        aria-hidden="true"
                                    />
                                    <ChatIcon :name="source.icon" :size="14" />
                                    <span>{{ source.label }}</span>
                                </button>
                            </div>
                        </label>
                        <label
                            class="tool-select"
                            :class="{ 'tool-select--active': isRegTypeActive }"
                            @click.stop
                        >
                            <ChatIcon name="car" :size="15" />
                            <button
                                type="button"
                                class="filter-trigger"
                                aria-label="Тип авто"
                                @click.stop="toggleDropdown('regType')"
                            >
                                {{ selectedRegTypeLabel }}
                            </button>
                            <button
                                v-if="isRegTypeActive"
                                type="button"
                                class="filter-reset"
                                aria-label="Сбросить тип авто"
                                @click.stop="resetFilter('regType')"
                            >
                                ×
                            </button>
                            <div
                                v-if="openDropdown === 'regType'"
                                class="filter-dropdown"
                                @click.stop
                            >
                                <button
                                    v-for="type in regTypeOptions"
                                    :key="type.value"
                                    type="button"
                                    class="filter-option"
                                    :class="{ 'filter-option--selected': localFilters.regType === type.value }"
                                    @click.stop="selectRegType(type.value)"
                                >
                                    <span>{{ type.label }}</span>
                                </button>
                            </div>
                        </label>
                    </div>

                    <div class="toolbar-right">
                        <span class="tokens">~{{ tokenCount }} ток.</span>
                        <ChatLayoutPicker
                            :model-value="localFilters.responseFormats"
                            @update:model-value="selectResponseFormats"
                        />
                    </div>
                </div>
            </div>

            <div v-if="isExpanded" class="disclaimer">
                AI-АНАЛИТИК МОЖЕТ ОШИБАТЬСЯ · ПРОВЕРЯЙТЕ ЦИФРЫ ПО ИСТОЧНИКАМ
            </div>
        </div>
    </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'
import ChatLayoutPicker from '@/components/chat/input/ChatLayoutPicker.vue'
import { estimateTokens } from '@/components/chat/mock/chatLimitsMock'
import {
    getAvailableSources,
    getChatRegionsCatalog,
    getChatRegionsCurrent,
    getChatResponseFormatCurrent,
} from '@/utils/chatApi'
import {
    formatSourcesFilterLabel,
    iconForSource,
    isAllSourcesFilter,
    isSourceFilterActive,
    labelForSource,
    normalizeSourceFilter,
    sourceIdsFromFilter,
} from '@/components/chat/state/useSourceSelection'
import { useChatStore } from '@/components/chat/state/useChatStore'

function draftPeriod() {
    const now = new Date()
    return {
        year: now.getFullYear(),
        monthFrom: 1,
        monthTo: now.getMonth() + 1,
    }
}

function isPeriodSet(period) {
    return period != null
        && Number.isFinite(Number(period?.year))
        && Number.isFinite(Number(period?.monthFrom))
        && Number.isFinite(Number(period?.monthTo))
}

function defaultFilters() {
    return {
        region: 'all',
        period: null,
        source: 'all',
        regType: 'all',
        responseFormats: [],
    }
}

/**
 * Legacy фоллбек, на случай если getAvailableSources() ещё не отработал.
 * Реальные лейблы берутся из ответа сервера (поле `label`) — см. sourceOptions.
 */
const SOURCE_LABELS = {
    all: 'Все источники',
    clickhouse_excel: 'Регистрации авто',
}

// Ключ прежнего локального хранилища произвольных регионов. Регионы теперь
// приходят только из справочника источников, остатки удаляем при монтировании.
const LEGACY_CUSTOM_REGIONS_KEY = 'chat_custom_regions'

const ALL_REGIONS_OPTION = { value: 'all', label: 'Все доступные' }

const monthOptions = [
    { value: 1, short: 'янв', label: 'Январь' },
    { value: 2, short: 'фев', label: 'Февраль' },
    { value: 3, short: 'мар', label: 'Март' },
    { value: 4, short: 'апр', label: 'Апрель' },
    { value: 5, short: 'май', label: 'Май' },
    { value: 6, short: 'июн', label: 'Июнь' },
    { value: 7, short: 'июл', label: 'Июль' },
    { value: 8, short: 'авг', label: 'Август' },
    { value: 9, short: 'сен', label: 'Сентябрь' },
    { value: 10, short: 'окт', label: 'Октябрь' },
    { value: 11, short: 'ноя', label: 'Ноябрь' },
    { value: 12, short: 'дек', label: 'Декабрь' },
]

const currentYear = new Date().getFullYear()
const yearOptions = Array.from({ length: 4 }, (_, index) => currentYear - index)

const regTypeOptions = [
    { value: 'all', label: 'Все авто' },
    { value: 'new', label: 'Новые' },
    { value: 'old', label: 'Б/У авто' },
]

function cloneFilters(value) {
    const defaults = defaultFilters()
    const period = isPeriodSet(value?.period)
        ? {
            year: Number(value.period.year),
            monthFrom: Number(value.period.monthFrom),
            monthTo: Number(value.period.monthTo),
        }
        : null
    return {
        region: value?.region || defaults.region,
        period,
        source: normalizeSourceFilter(value?.source, defaults.source),
        regType: value?.regType || defaults.regType,
        responseFormats: Array.isArray(value?.responseFormats)
            ? [...value.responseFormats]
            : defaults.responseFormats,
    }
}

function dropLegacyCustomRegions() {
    if (typeof localStorage === 'undefined') return
    localStorage.removeItem(LEGACY_CUSTOM_REGIONS_KEY)
}

export default {
    name: 'ChatInputArea',
    components: { ChatIcon, ChatLayoutPicker },
    props: {
        chips: { type: Array, default: () => [] },
        isStreaming: { type: Boolean, default: false },
        isLimitReached: { type: Boolean, default: false },
        placeholder: { type: String, default: 'Спросите про авторынок...' },
        hasMessages: { type: Boolean, default: false },
        filterSelection: {
            type: Object,
            default: defaultFilters,
        },
    },
    emits: ['send', 'stop', 'remove-chip', 'update-filters'],
    setup(props, { emit, expose }) {
        const text = ref('')
        const textareaRef = ref(null)
        const cardRef = ref(null)
        const regionInputRef = ref(null)
        const isFocused = ref(false)
        const manualExpanded = ref(false)
        const pointerStartedInside = ref(false)
        const availableSources = ref([])
        const regionSearch = ref('')
        const openDropdown = ref(null)
        // Регионы для селектора: справочник источников, суженный до набора
        // тарифа, если тариф ограничивает регионы. Приходит с бэкенда.
        const tariffRegions = ref([])
        const regionRestricted = ref(false)
        const regionsLoaded = ref(false)
        const localFilters = reactive({
            region: 'all',
            period: null,
            source: 'all',
            regType: 'all',
            responseFormats: [],
        })
        const periodDraft = reactive(draftPeriod())

        const allRegionOptions = computed(() => [
            ALL_REGIONS_OPTION,
            ...tariffRegions.value.map(name => ({ value: name, label: name })),
        ])

        const filteredRegionOptions = computed(() => {
            const query = regionSearch.value.trim().toLowerCase()
            if (!query) return allRegionOptions.value
            return allRegionOptions.value.filter(region => region.label.toLowerCase().includes(query))
        })

        const regionEmptyHint = computed(() => {
            if (!regionsLoaded.value) return 'Загрузка регионов…'
            if (!tariffRegions.value.length) {
                return regionRestricted.value ? 'Нет регионов в тарифе' : 'Справочник регионов пуст'
            }
            return 'Регион не найден'
        })

        const sourceOptions = computed(() => {
            // OCP: список источников полностью драйвится ответом сервера.
            // Никаких whitelist'ов по коду — кто доступен пользователю по плану,
            // тот и виден. Метаданные (icon/label) берём из useSourceSelection.
            const sources = availableSources.value.map(source => ({
                id: source.id,
                label: labelForSource(source) || SOURCE_LABELS[source.id] || source.label || source.id,
                icon: iconForSource(source),
                kind: source.kind,
            }))

            const fallback = sources.length
                ? sources
                : [{ id: 'clickhouse_excel', label: SOURCE_LABELS.clickhouse_excel, icon: 'database', kind: 'internal' }]
            return [{ id: 'all', label: SOURCE_LABELS.all, icon: 'database', kind: 'internal' }, ...fallback]
        })

        const isExpanded = computed(() => {
            if (props.isStreaming) return false
            if (isFocused.value) return true
            if (text.value.trim().length > 0) return true
            if (manualExpanded.value) return true
            return !props.hasMessages
        })

        const tokenCount = computed(() => {
            if (!text.value) return 0
            return estimateTokens(text.value)
        })

        const canSend = computed(() => {
            return Boolean(text.value.trim()) && !props.isStreaming && !props.isLimitReached
        })

        const isRegionActive = computed(() => localFilters.region !== defaultFilters().region)
        const isPeriodActive = computed(() => isPeriodSet(localFilters.period))
        const isSourceActive = computed(() => isSourceFilterActive(localFilters.source))
        const isRegTypeActive = computed(() => localFilters.regType !== defaultFilters().regType)
        const selectedRegionLabel = computed(() => {
            return allRegionOptions.value.find(region => region.value === localFilters.region)?.label || localFilters.region
        })
        const selectedSourceIds = computed(() => sourceIdsFromFilter(localFilters.source))

        const selectedSourceLabel = computed(() => {
            const ids = selectedSourceIds.value
            if (ids.length === 0) return SOURCE_LABELS.all
            const resolveLabel = id => sourceOptions.value.find(source => source.id === id)?.label
                || SOURCE_LABELS[id]
                || id
            return formatSourcesFilterLabel(ids, resolveLabel)
        })
        const selectedSourceIcon = computed(() => {
            const ids = selectedSourceIds.value
            if (ids.length !== 1) return 'database'
            return sourceOptions.value.find(source => source.id === ids[0])?.icon || 'database'
        })
        const selectedRegTypeLabel = computed(() => {
            return regTypeOptions.find(type => type.value === localFilters.regType)?.label || 'Все авто'
        })
        const selectedPeriodLabel = computed(() => {
            if (!isPeriodSet(localFilters.period)) {
                return 'Период'
            }
            const from = monthOptions.find(month => month.value === localFilters.period.monthFrom)?.short || localFilters.period.monthFrom
            const to = monthOptions.find(month => month.value === localFilters.period.monthTo)?.short || localFilters.period.monthTo
            return localFilters.period.monthFrom === localFilters.period.monthTo
                ? `${from} ${localFilters.period.year}`
                : `${from}-${to} ${localFilters.period.year}`
        })

        function autosize() {
            const el = textareaRef.value
            if (!el) return
            el.style.height = 'auto'
            el.style.height = Math.min(el.scrollHeight, 180) + 'px'
        }

        function syncLocalFilters(value) {
            const next = cloneFilters(value)
            localFilters.region = next.region
            localFilters.period = next.period ? { ...next.period } : null
            if (next.period) {
                periodDraft.year = next.period.year
                periodDraft.monthFrom = next.period.monthFrom
                periodDraft.monthTo = next.period.monthTo
            }
            localFilters.source = next.source
            localFilters.regType = next.regType
            localFilters.responseFormats = next.responseFormats
        }

        function commitPeriodFilter() {
            localFilters.period = {
                year: periodDraft.year,
                monthFrom: periodDraft.monthFrom,
                monthTo: periodDraft.monthTo,
            }
            emitFilters()
        }

        function emitFilters() {
            emit('update-filters', cloneFilters(localFilters))
        }

        function toggleDropdown(name) {
            openDropdown.value = openDropdown.value === name ? null : name
            if (openDropdown.value === 'region') {
                regionSearch.value = ''
                nextTick(() => regionInputRef.value?.focus())
            }
            if (openDropdown.value === 'period' && isPeriodSet(localFilters.period)) {
                periodDraft.year = localFilters.period.year
                periodDraft.monthFrom = localFilters.period.monthFrom
                periodDraft.monthTo = localFilters.period.monthTo
            }
        }

        function closeDropdown() {
            openDropdown.value = null
            regionSearch.value = ''
        }

        function onDocumentPointerDown(event) {
            if (!openDropdown.value) return
            if (cardRef.value?.contains(event.target)) return
            closeDropdown()
        }

        function selectRegion(value) {
            localFilters.region = value
            closeDropdown()
            emitFilters()
        }

        function isSourceOptionSelected(id) {
            if (id === 'all') return isAllSourcesFilter(localFilters.source)
            return selectedSourceIds.value.includes(id)
        }

        function toggleSource(id) {
            if (id === 'all') {
                localFilters.source = 'all'
                emitFilters()
                return
            }
            const current = selectedSourceIds.value
            if (isAllSourcesFilter(localFilters.source)) {
                localFilters.source = [id]
            } else if (current.includes(id)) {
                const next = current.filter(item => item !== id)
                localFilters.source = next.length === 0 ? 'all' : next
            } else {
                localFilters.source = [...current, id]
            }
            emitFilters()
        }

        function selectRegType(value) {
            localFilters.regType = value
            closeDropdown()
            emitFilters()
        }

        function selectResponseFormats(value) {
            localFilters.responseFormats = Array.isArray(value) ? value : []
            emitFilters()
        }

        function selectFirstRegion() {
            const first = filteredRegionOptions.value[0]
            if (first) selectRegion(first.value)
        }

        function resetFilter(filterName) {
            if (filterName === 'region') {
                localFilters.region = defaultFilters().region
            } else if (filterName === 'period') {
                localFilters.period = null
                closeDropdown()
            } else if (filterName === 'source') {
                localFilters.source = 'all'
            } else if (filterName === 'regType') {
                localFilters.regType = defaultFilters().regType
            }
            emitFilters()
        }

        function onInput() {
            nextTick(autosize)
        }

        function onFocus() {
            isFocused.value = true
            manualExpanded.value = true
            nextTick(autosize)
        }

        function onPeriodYearChange() {
            commitPeriodFilter()
        }

        function onMonthFromChange() {
            if (periodDraft.monthFrom > periodDraft.monthTo) {
                periodDraft.monthTo = periodDraft.monthFrom
            }
            commitPeriodFilter()
        }

        function onMonthToChange() {
            if (periodDraft.monthTo < periodDraft.monthFrom) {
                periodDraft.monthFrom = periodDraft.monthTo
            }
            commitPeriodFilter()
        }

        function onCardPointerDown() {
            pointerStartedInside.value = true
            window.setTimeout(() => {
                pointerStartedInside.value = false
            }, 0)
        }

        function onBlur(event) {
            const nextTarget = event.relatedTarget
            if (pointerStartedInside.value || (nextTarget && cardRef.value?.contains(nextTarget))) {
                isFocused.value = false
                return
            }
            isFocused.value = false
            closeDropdown()
            if (!text.value.trim()) {
                manualExpanded.value = false
            }
        }

        function expand() {
            if (props.isLimitReached) return
            manualExpanded.value = true
            nextTick(() => {
                textareaRef.value?.focus()
                autosize()
            })
        }

        function send() {
            const value = text.value.trim()
            if (!value || !canSend.value) return
            emit('send', value)
            text.value = ''
            manualExpanded.value = false
            nextTick(() => {
                autosize()
                textareaRef.value?.blur()
            })
        }

        function onKeydown(event) {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                send()
            }
        }

        watch(
            () => props.isLimitReached,
            reached => {
                if (reached) {
                    text.value = ''
                    manualExpanded.value = false
                    nextTick(autosize)
                }
            }
        )

        watch(
            () => props.isStreaming,
            streaming => {
                if (streaming) {
                    manualExpanded.value = false
                    textareaRef.value?.blur()
                }
            }
        )

        watch(
            () => props.filterSelection,
            value => syncLocalFilters(value),
            { deep: true, immediate: true }
        )

        onMounted(async () => {
            document.addEventListener('pointerdown', onDocumentPointerDown)
            dropLegacyCustomRegions()
            try {
                availableSources.value = await getAvailableSources()
                // Регистрируем источники в store, чтобы buildFilterChips мог
                // отрисовать корректный лейбл/иконку для любого выбранного kind.
                useChatStore().registerAvailableSources(availableSources.value)
            } catch (error) {
                availableSources.value = []
            }
            await loadRegionOptions()
            await loadResponseFormat()
        })

        /**
         * Сохранённые шаблоны ответа пользователя. Гость получит 401 — тогда
         * остаётся «Авто» (или то, что уже выбрано в этой сессии).
         */
        async function loadResponseFormat() {
            try {
                const data = await getChatResponseFormatCurrent()
                const saved = data?.response_formats
                if (Array.isArray(saved) && saved.join(',') !== localFilters.responseFormats.join(',')) {
                    localFilters.responseFormats = saved
                    emitFilters()
                }
            } catch (error) {
                // noop: у гостя выбор живёт только в состоянии стора.
            }
        }

        /**
         * Регионы селектора приходят только с бэкенда: набор тарифа, если тариф
         * ограничивает регионы, иначе справочник источников. Для гостей —
         * публичный каталог.
         */
        async function loadRegionOptions() {
            let data = null
            try {
                data = await getChatRegionsCurrent()
            } catch (error) {
                try {
                    data = await getChatRegionsCatalog()
                } catch (fallbackError) {
                    data = null
                }
            }

            const allowed = Array.isArray(data?.allowed_regions)
                ? data.allowed_regions.map(region => region.name).filter(Boolean)
                : []
            regionRestricted.value = Boolean(data?.restricted)
            tariffRegions.value = allowed
            regionsLoaded.value = true

            // Сброс выбора, которого нет в актуальном списке (сменился тариф,
            // либо в фильтре осталось произвольное значение прежней версии).
            if (localFilters.region !== 'all' && !allowed.includes(localFilters.region)) {
                localFilters.region = 'all'
                emitFilters()
            }
        }

        onBeforeUnmount(() => {
            document.removeEventListener('pointerdown', onDocumentPointerDown)
        })

        expose({
            focus() {
                manualExpanded.value = true
                nextTick(() => textareaRef.value?.focus())
            },
            setText(value) {
                text.value = value
                manualExpanded.value = true
                nextTick(() => {
                    autosize()
                    textareaRef.value?.focus()
                })
            },
        })

        return {
            cardRef,
            textareaRef,
            regionInputRef,
            text,
            localFilters,
            regionSearch,
            openDropdown,
            sourceOptions,
            allRegionOptions,
            filteredRegionOptions,
            regionEmptyHint,
            periodDraft,
            monthOptions,
            yearOptions,
            regTypeOptions,
            isExpanded,
            tokenCount,
            canSend,
            isRegionActive,
            isPeriodActive,
            isSourceActive,
            isRegTypeActive,
            selectedRegionLabel,
            selectedSourceLabel,
            selectedSourceIcon,
            selectedRegTypeLabel,
            selectedPeriodLabel,
            emitFilters,
            toggleDropdown,
            selectRegion,
            isSourceOptionSelected,
            toggleSource,
            selectRegType,
            selectResponseFormats,
            selectFirstRegion,
            resetFilter,
            onInput,
            onFocus,
            onBlur,
            expand,
            send,
            onKeydown,
            onPeriodYearChange,
            onMonthFromChange,
            onMonthToChange,
            onCardPointerDown,
        }
    },
}
</script>

<style scoped>
.input-wrap {
    padding: 8px 20px 10px;
    background: white;
    border-top: 1px solid #e7e7e7;
    transition: padding 0.2s ease;
}

.input-wrap--expanded {
    padding: 12px 20px 14px;
}

.input-inner {
    max-width: 760px;
    width: 100%;
    margin: 0 auto;
}

.input-card {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    padding: 4px 6px 4px 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
    transition: border-color 0.15s ease, box-shadow 0.15s ease, padding 0.15s ease;
    cursor: text;
}

.input-card--expanded {
    padding: 12px 14px 10px;
    cursor: default;
}

.input-card:focus-within,
.input-card--expanded {
    border-color: #23282C;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.input-card--disabled {
    background: #f7f7f7;
    opacity: 0.75;
    cursor: not-allowed;
}

.input-row {
    display: flex;
    align-items: flex-end;
    gap: 8px;
}

.expand-btn {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 0;
    background: transparent;
    color: #888888;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.expand-btn:hover {
    background: #e7e7e7;
    color: #23282C;
}

.input-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 8px;
}

.input-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 14px;
    border: 1px solid #e0e0e0;
    background: white;
    font-size: 11px;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    color: #23282C;
}

.chip-remove {
    background: transparent;
    border: 0;
    color: #888888;
    cursor: pointer;
    font-size: 14px;
    line-height: 1;
    padding: 0 2px;
}

.chip-remove:hover {
    color: #23282C;
}

.input-field {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    resize: none;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.45;
    color: #23282C;
    background: transparent;
    padding: 6px 0;
    min-height: 20px;
    max-height: 180px;
}

.input-field--compact {
    padding: 4px 4px;
    min-height: 22px;
    line-height: 1.4;
}

.input-field::placeholder {
    color: #B4B2A9;
}

.input-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 6px;
    gap: 10px;
    min-width: 0;
    position: relative;
    overflow: visible;
}

.toolbar-left {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 0;
    overflow: visible;
    white-space: nowrap;
}

.tool-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: inherit;
    font-size: 11.5px;
    padding: 5px 10px;
    border-radius: 14px;
    border: 1px solid #e0e0e0;
    background: white;
    color: #23282C;
    cursor: pointer;
}

.tool-btn:hover {
    border-color: #23282C;
}

.tool-select {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
    position: relative;
    min-height: 28px;
    padding: 4px 8px;
    border-radius: 14px;
    border: 1px solid #e0e0e0;
    background: #f7f7f7;
    color: #888888;
}

.tool-select:hover,
.tool-select:focus-within {
    border-color: #23282C;
}

.tool-select--active {
    border-color: #23282C;
    background: #e7e7e7;
    color: #23282C;
}

.filter-trigger {
    border: 0;
    background: transparent;
    color: inherit;
    font-family: inherit;
    font-size: 11.5px;
    padding: 0;
    cursor: pointer;
}

.filter-dropdown {
    position: absolute;
    left: 0;
    bottom: calc(100% + 8px);
    z-index: 100;
    width: 260px;
    max-height: 280px;
    overflow-y: auto;
    padding: 8px;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    background: white;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
    color: #23282C;
}

.filter-dropdown--period {
    width: 280px;
}

.region-search {
    width: 100%;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    outline: 0;
    padding: 7px 9px;
    font-family: inherit;
    font-size: 12px;
    color: #23282C;
}

.region-search:focus {
    border-color: #23282C;
}

.region-search-hint {
    margin: 4px 0 0;
    font-size: 10px;
    line-height: 1.3;
    color: #A8A6A0;
}

.filter-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    margin-top: 6px;
    padding: 7px 8px 7px 9px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #23282C;
    font-family: inherit;
    font-size: 12px;
    text-align: left;
    cursor: pointer;
}

.filter-option--multi {
    justify-content: flex-start;
}

.filter-option-check {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    border: 1px solid #C4C2BC;
    border-radius: 3px;
    background: white;
}

.filter-option-check--on {
    border-color: #23282C;
    background: #23282C;
    box-shadow: inset 0 0 0 2px white;
}

.filter-option:hover,
.filter-option--selected {
    background: #f7f7f7;
}

.period-grid {
    display: grid;
    gap: 8px;
}

.period-field {
    display: grid;
    gap: 4px;
    font-size: 11px;
    color: #888888;
}

.dropdown-select {
    width: 100%;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    outline: 0;
    background: white;
    color: #23282C;
    font-family: inherit;
    font-size: 12px;
    padding: 7px 9px;
}

.dropdown-select:focus {
    border-color: #23282C;
}

.filter-reset {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #888888;
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
}

.filter-reset:hover {
    background: white;
    color: #23282C;
}

.toolbar-right {
    display: flex;
    align-items: center;
    gap: 10px;
}

.tokens {
    font-size: 11px;
    color: #888888;
}

.send-btn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 6px;
    border: 0;
    background: #23282C;
    color: white;
    cursor: pointer;
    transition: background-color 0.15s ease, opacity 0.15s ease, transform 0.12s ease, box-shadow 0.15s ease;
}

.send-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.send-btn:not(:disabled):hover {
    background: #000;
}

.send-btn--stop {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #E30613;
    box-shadow: 0 2px 10px rgba(227, 6, 19, 0.32);
}

.send-btn--stop:hover {
    background: #c30410;
    box-shadow: 0 3px 14px rgba(227, 6, 19, 0.42);
}

.send-btn--stop:active {
    transform: scale(0.96);
}

.disclaimer {
    margin-top: 8px;
    text-align: center;
    font-size: 10px;
    color: #888888;
    letter-spacing: 0.5px;
    text-transform: uppercase;
}

@media (max-width: 768px) {
    .input-wrap {
        padding: 8px 14px 10px;
    }

    .input-wrap--expanded {
        padding: 12px 14px 14px;
    }

    .tool-btn {
        width: 32px;
        height: 32px;
        padding: 0;
        justify-content: center;
        border-radius: 8px;
    }

    .tool-label {
        display: none;
    }

    .tool-select {
        height: 32px;
        border-radius: 8px;
    }

    .filter-dropdown {
        width: min(280px, calc(100vw - 28px));
    }
}
</style>
